import styled, { keyframes } from 'styled-components';
import { Input, Table } from 'reactstrap';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const QuestionsContainer = styled.div`
  padding: 20px;
  padding-bottom: 0;
  animation: ${fadeIn} 0.6s ease-out;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  
  /* Loading spinner animation */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const FormWrapper = styled.div`
  margin-bottom: 20px;
  border: 2px solid #ccc;
  padding: 15px;
  background-color: #f9f9f9;
`;

export const StyledButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #007bff);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: linear-gradient(135deg, #6c757d, #5a6268);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const StyledTableHeader = styled.th`
  background-color: #f0f0f0;
  color: #333;
`;

export const HeaderButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;
  gap: 15px;
  flex-wrap: wrap;

  .buttons-and-search {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .filters-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

export const FilterInput = styled(Input)`
  width: 300px;
  padding: 10px 15px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: #999;
  }

  &:hover {
    border-color: #007bff;
  }

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
  align-items: center;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-weight: 600;
    font-size: 13px;
    color: #333;
    white-space: nowrap;
  }

  select {
    padding: 8px 10px;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 13px;
    min-width: 120px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #007bff;
    }

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      outline: none;
    }
  }
`;

export const TableContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

export const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f0f0f0;
    border-bottom: 2px solid #ccc;
    padding: 8px;
    text-align: left;
  }

  tbody tr:nth-child(odd) {
    background-color: #f0f8ff;
  }

  tbody tr:nth-child(even) {
    background-color: white;
  }

  tbody td {
    padding: 8px;
    max-width: 300px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    border: 0.5px solid #ccc;
    vertical-align: top;
  }

  .action-buttons {
    display: flex;
    gap: 5px;
  }
`;

export const QuestionContent = styled.div`
  max-height: 150px;
  overflow-y: auto;
  padding: 5px;
  background-color: #f9f9f9;
  border-radius: 3px;
  font-size: 13px;
  line-height: 1.4;
`;

export const ContentBox = styled.div`
  background: #f5f8ff;
  border: 1px solid #d0e2ff;
  border-radius: 6px;
  padding: 10px 12px;
  color: #1f2937;
  line-height: 1.6;
`;

export const ExplanationBox = styled.div`
  margin-top: 8px;
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 6px;
  padding: 10px 12px;
  color: #5d4037;
  line-height: 1.6;
`;

export const ToggleExplanationButton = styled.button`
  margin-top: 6px;
  background: none;
  border: none;
  color: #0d6efd;
  cursor: pointer;
  font-size: 13px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const DifficultyBadge = styled.span<{ difficulty: number }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  background-color: ${props => 
    props.difficulty === 1 ? '#28a745' : 
    props.difficulty === 2 ? '#ffc107' : 
    '#dc3545'
  };
`;

export const QuestionCountContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  font-size: 14px;
  margin-bottom: 15px;
  animation: ${fadeIn} 0.6s ease-out;

  .search-wrapper {
    position: relative;
    flex-shrink: 0;
    order: -1;
  }

  .pagination-info-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .pagination-buttons {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .total-info {
    color: #007bff;
    font-weight: bold;
    white-space: nowrap;
    font-size: 13px;
  }
`;

export const SearchInput = styled(Input)`
  width: 320px;
  padding: 10px 16px;
  font-size: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  outline: none;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.08);

  &::placeholder {
    color: #aaa;
    font-weight: 500;
  }

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 12px rgba(0, 123, 255, 0.15);
  }

  &:focus {
    border-color: #0056b3;
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.15), 0 2px 12px rgba(0, 123, 255, 0.2);
  }
`;

export const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 7px 14px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #0056b3, #004085);
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 123, 255, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: linear-gradient(135deg, #9ca3af, #6b7280);
    opacity: 0.6;
    box-shadow: none;
  }
`;

export const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 200px;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.25);
  }
`;

export const JsonEditor = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 2px solid #007bff;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  resize: vertical;
  min-height: 120px;
  background-color: #f5f5f5;

  &:focus {
    border-color: #0056b3;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 91, 187, 0.5);
  }
`;

export const SampleJsonEditor = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 2px solid #28a745;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  resize: vertical;
  min-height: 120px;
  background-color: #f0fff4;
  color: #155724;

  &:focus {
    border-color: #20c997;
    outline: none;
    box-shadow: 0 0 5px rgba(40, 167, 69, 0.5);
  }
`;

export const DropRightContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const DropRightButton = styled.button`
  padding: 8px 12px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.4);
  }

  &::after {
    content: '▼';
    font-size: 10px;
    margin-left: 2px;
  }
`;

export const DropRightPanel = styled.div<{ isOpen: boolean }>`
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 8px;
  background: white;
  border: 2px solid #007bff;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 180px;
  max-width: 400px;
  width: max-content;
  max-height: 400px;
  overflow-y: auto;
  display: ${(p) => (p.isOpen ? 'block' : 'none')};
  animation: ${(p) => (p.isOpen ? 'slideDown 0.3s ease' : 'none')};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #007bff;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #0056b3;
  }

  @keyframes slideDown {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export const DropRightTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DropRightTag = styled.div<{ isActive: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  background: ${(p) => 
    p.isActive 
      ? 'linear-gradient(135deg, #007bff, #0056b3)' 
      : 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
  };
  color: ${(p) => (p.isActive ? '#fff' : '#495057')};
  font-weight: 600;
  box-shadow: ${(p) => 
    p.isActive 
      ? '0 2px 4px rgba(0, 123, 255, 0.3)' 
      : '0 1px 2px rgba(0, 0, 0, 0.1)'
  };
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(3px);
  }
`;

export const SelectTag = styled.div<{ isActive: boolean }>`
  padding: 6px 12px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  background: ${(p) => 
    p.isActive 
      ? 'linear-gradient(135deg, #007bff, #0056b3)' 
      : 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
  };
  color: ${(p) => (p.isActive ? '#fff' : '#495057')};
  font-weight: 600;
  box-shadow: ${(p) => 
    p.isActive 
      ? '0 3px 6px rgba(0, 123, 255, 0.3)' 
      : '0 2px 4px rgba(0, 0, 0, 0.1)'
  };
  transform: ${(p) => (p.isActive ? 'translateY(-2px)' : 'translateY(0)')};
  transition: all 0.3s ease;
  user-select: none;
  
  &:hover {
    background: ${(p) => 
      p.isActive 
        ? 'linear-gradient(135deg, #0056b3, #007bff)' 
        : 'linear-gradient(135deg, #e9ecef, #dee2e6)'
    };
    box-shadow: ${(p) => 
      p.isActive 
        ? '0 5px 10px rgba(0, 123, 255, 0.4)' 
        : '0 4px 8px rgba(0, 0, 0, 0.15)'
    };
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const TagsContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

export const SectionLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
`;

// Modal Button Styles
export const WarningButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #ffc107, #ff9800);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #ff9800, #ffc107);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 193, 7, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DangerButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #c82333, #dc3545);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(220, 53, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const PrimaryButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #007bff);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: linear-gradient(135deg, #6c757d, #5a6268);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #6c757d, #545b62);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #545b62, #6c757d);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(108, 117, 125, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const SuccessButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #20c997, #28a745);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(40, 167, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const InfoButton = styled.button`
  padding: 6px 12px;
  background: linear-gradient(135deg, #17a2b8, #138496);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(23, 162, 184, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #138496, #17a2b8);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(23, 162, 184, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;


export const StyledSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
  }

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    outline: none;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

// Selected Questions Panel
export const SelectedQuestionsPanel = styled.div`
  position: fixed;
  right: 20px;
  top: 120px;
  width: 320px;
  max-height: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 1000;
  overflow-y: auto;
`;

export const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
`;

export const PanelTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
`;

export const ClearAllButton = styled.button`
  padding: 4px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #c82333;
    transform: scale(1.05);
  }
`;

export const SelectedQuestionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #e9ecef;
    transform: translateX(-2px);
  }
`;

export const QuestionInfo = styled.div`
  flex: 1;
  font-size: 13px;
  color: #495057;
  
  strong {
    color: #007bff;
    font-weight: 600;
  }
`;

export const RemoveButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  color: #dc3545;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ffe0e0;
    transform: scale(1.2);
  }
`;

// Floating Add to Exam Button
export const FloatingAddButton = styled.button`
  position: fixed;
  bottom: 40px;
  left: 40px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #28a745, #218838);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(40, 167, 69, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 12px 32px rgba(40, 167, 69, 0.6);
  }

  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

export const FloatingDeleteButton = styled.button`
  position: fixed;
  bottom: 40px;
  right: 40px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(220, 53, 69, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 12px 32px rgba(220, 53, 69, 0.6);
  }

  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

export const ButtonBadge = styled.span`
  padding: 4px 10px;
  background: white;
  color: #28a745;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
`;

// Add to Exam Modal Styles
export const ExamModalContent = styled.div`
  padding: 20px 0;
`;

export const ExamCard = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa, #ffffff);
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #007bff;
    background: linear-gradient(135deg, #e7f3ff, #ffffff);
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
  }
`;

export const ExamCardTitle = styled.h5`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
`;

export const ExamCardMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: #6c757d;
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  
  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

